import { putRecyclingPackageProductAPI } from '@/src/apis/rest/recyclingProducts';
import { PutRecyclingPackageProductReqeustBody } from '@/src/apis/rest/recyclingProducts.dtos';
import Button from '@/src/components/atom/Button';
import Dialog from '@/src/components/atom/Dialog';
import Input from '@/src/components/atom/Input';
import RadioGroup from '@/src/components/atom/RadioGroup';
import FormContent from '@/src/components/molecule/FormContent';
import { Form, FormField } from '@/src/components/ui/form';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { OPTIONS_CONST } from '@/src/constants/options';
import { TOAST_CONST } from '@/src/constants/toast';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { usePrompt } from '@/src/hooks/usePrompt';
import { toast } from '@/src/hooks/useToast';
import { RecyclingPackageProductDto } from '@/src/models/RecyclingProductDto';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

interface RecyclingPackageProductEditModalProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  onOk: () => void;
  data: RecyclingPackageProductDto;
  unitList: { key: string; value: string }[];
}

const formSchema = () => {
  return z.object({
    name: z.string().min(1),
    weight: z.number(),
    maximumWeight: z.number(),
    carbonEmissionReduction: z.number(),
    pineScore: z.number(),
    unit: z.string(),
    isForApp: z.boolean().optional(),
  });
};

const RecyclingPackageProductEditModal = ({
  open: openProps,
  onOpenChange: onOpenChangeProps,
  onOk,
  data,
  unitList,
}: RecyclingPackageProductEditModalProps) => {
  const {
    Dialog: BlockAlertDialog,
    isOpen: isBlockAlertOpen,
    openDialog: openBlockAlertDialog,
    closeDialog: closeBlockAlertDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });

  const {
    Dialog: EditDialog,
    isOpen: isEditOpen,
    openDialog: openEditDialog,
    closeDialog: closeEditDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });

  const formRef = useRef<HTMLFormElement>(null);

  const form = useForm<PutRecyclingPackageProductReqeustBody>({
    resolver: zodResolver(formSchema()),
    defaultValues: {
      ...data,
    },
    reValidateMode: 'onSubmit',
  });

  const putRecyclingPackageProductMutation = useMutation({
    mutationFn: putRecyclingPackageProductAPI,
  });

  const handleSubmit = (values: PutRecyclingPackageProductReqeustBody) => {
    putRecyclingPackageProductMutation.mutate(
      { ...values, id: data.id },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });

          onOpenChangeProps(false);
          onOk();
        },
        onError: e => {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        },
      },
    );
  };

  const handleBlockModalOk = () => {
    onOpenChangeProps(false);
  };

  const handleCloseModal = () => {
    if (form.formState.isDirty) {
      openBlockAlertDialog();
    } else {
      onOpenChangeProps(false);
    }
  };

  usePrompt(form.formState.isDirty && !form.formState.isSubmitted);

  const renderFooter = () => {
    return (
      <div className="flex justify-end gap-2">
        <Button
          className="flex-1"
          buttonColor="outline"
          onClick={handleCloseModal}
        >
          {BUTTON_CONST.cancel}
        </Button>
        <Button
          key={String(form.formState.isValid)}
          type="button"
          className="flex-1"
          disabled={!form.formState.isValid}
          onClick={openEditDialog}
          isLoading={putRecyclingPackageProductMutation.isPending}
        >
          {BUTTON_CONST.save}
        </Button>
      </div>
    );
  };

  return data ? (
    <>
      <Dialog
        open={openProps}
        onOpenChange={handleCloseModal}
        contentProps={{
          style: {
            width: 'calc(100vw - 24px)',
          },
          className: 'max-w-[697px]',
        }}
        title="수거 기본 품목 수정"
        footer={renderFooter()}
      >
        <Form {...form}>
          <form
            ref={formRef}
            className="flex flex-col gap-6"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="수거 품목명" isRequired error={error}>
                    <Input
                      onClear={() => field.onChange('')}
                      placeholder="수거 품목 명을 입력해 주세요"
                      {...field}
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="weight"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="무게 (g)" isRequired error={error}>
                    <Input
                      placeholder="무게를 입력해 주세요"
                      {...field}
                      onClear={() => field.onChange('')}
                      type="number"
                      min={0}
                      onChange={e =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : '',
                        )
                      }
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="carbonEmissionReduction"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="탄소 감축량 (g)" isRequired error={error}>
                    <Input
                      placeholder="탄소 감축량을 입력해 주세요"
                      {...field}
                      onClear={() => field.onChange('')}
                      type="number"
                      min={0}
                      onChange={e =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : '',
                        )
                      }
                      isDecimal
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="pineScore"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="소나무 지수" isRequired error={error}>
                    <Input
                      placeholder="소나무 지수를 입력해 주세요"
                      {...field}
                      onClear={() => field.onChange('')}
                      type="number"
                      min={0}
                      onChange={e =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : '',
                        )
                      }
                      isDecimal
                    />
                  </FormContent>
                );
              }}
            />
            {unitList && (
              <FormField
                control={form.control}
                name="unit"
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormContent label="단위" error={error}>
                      <RadioGroup
                        optionList={unitList.map((item: any) => ({
                          label: item.value,
                          value: item.key,
                        }))}
                        rootProps={{
                          value: field.value,
                          onValueChange: (e: string) => {
                            field.onChange(e);
                          },
                        }}
                      />
                    </FormContent>
                  );
                }}
              />
            )}
            <FormField
              control={form.control}
              name="maximumWeight"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="최대 무게 (g)" isRequired error={error}>
                    <Input
                      placeholder="1개의 수거 기본 품목 당 최대 수용 무게를 입력해 주세요"
                      {...field}
                      onClear={() => field.onChange('')}
                      type="number"
                      min={0}
                      onChange={e =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : '',
                        )
                      }
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="isForApp"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="앱 내 적용 여부" error={error}>
                    <RadioGroup
                      optionList={[
                        { label: OPTIONS_CONST.applied, value: 'true' },
                        { label: OPTIONS_CONST.notApplied, value: 'false' },
                      ]}
                      rootProps={{
                        value: field.value ? 'true' : 'false',
                        onValueChange: (e: string) =>
                          field.onChange(e === 'true'),
                      }}
                    />
                  </FormContent>
                );
              }}
            />
          </form>
        </Form>

        <BlockAlertDialog
          open={isBlockAlertOpen}
          onOpenChange={closeBlockAlertDialog}
          onCancel={() => {
            closeBlockAlertDialog();
          }}
          onOk={handleBlockModalOk}
          title="취소"
        >
          변경사항은 저장되지 않습니다. 취소하시겠습니까?
        </BlockAlertDialog>
      </Dialog>

      {isEditOpen && (
        <EditDialog
          open={isEditOpen}
          onOk={() => {
            handleSubmit(form.watch());
          }}
          onOpenChange={e => {
            if (e) {
              openEditDialog();
            } else {
              closeEditDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.edit}
          isLoading={putRecyclingPackageProductMutation.isPending}
        />
      )}
    </>
  ) : null;
};

export default RecyclingPackageProductEditModal;
